//
// Copyright (c) 2011-2024 by Curalate, Inc.
//

import React, { useState } from 'react';
import styled from 'styled-components';

import { PrimaryButton } from '@components/atoms/button/Button';
import { BodyText, TitleText } from '@components/atoms/text/Text';
import OwnedImage from '@images/owned_rights.svg';
import ApprovedImage from '@images/preapproved_rights.svg';
import Cuid from '@models/Cuid';
import { RightsCreationSource } from '@models/RightsCreationSource';
import { UIA_RIGHTS_FLOW_TRACKING_IDS } from '@uia/rights/constants/uiaRightsConstants';
import { ExpiryInfo } from '@uia/rights/rights-expiry/ExpiryInfo';
import { UIARightsSetExpiryScreen } from '@uia/rights/rights-expiry/UIARightsSetExpiryScreen';
import { isDefined, isUndefined } from '@utils/filterPredicates';

import { RIGHTS_STATUS } from 'app/bulk-actions/bulk-actions-constants/bulk-actions-constants';

const rightsStatusConfigs = [
  {
    description:
      "No need to request rights.\nYou're free to add some products,\nand distribute this everywhere!",
    icon: OwnedImage,
    source: RightsCreationSource.ApprovedRightsRequest,
    status: RIGHTS_STATUS.BRAND_OWNED,
    title: 'Rights Owned',
  },
  {
    description:
      "This media belongs to a brand partner that you're collaborating with. You're free to add some products and distribute this everywhere!",
    icon: ApprovedImage,
    source: RightsCreationSource.RelationshipClientToClient,
    status: RIGHTS_STATUS.APPROVED,
    title: 'Rights Approved',
  },
  {
    description:
      "This media belongs to a creator that you've configured auto-rights approval on. You're free to add some products and distribute this everywhere!",
    icon: ApprovedImage,
    source: RightsCreationSource.RelationshipInfluencerToClient,
    status: RIGHTS_STATUS.APPROVED,
    title: 'Rights Approved',
  },
  {
    description:
      "The rights request has been approved. You're free to add some products and distribute this everywhere!",
    icon: ApprovedImage,
    source: RightsCreationSource.ApprovedRightsRequest,
    status: RIGHTS_STATUS.APPROVED,
    title: 'Rights Approved',
  },
  {
    description:
      "This media is imported from Bazaarvoice with rights approved. You're free to add some products and distribute this everywhere!",
    icon: ApprovedImage,
    isBVImport: true,
    source: RightsCreationSource.SkippedRightsRequest,
    status: RIGHTS_STATUS.APPROVED,
    title: 'Rights Approved',
  },
  {
    description:
      "The rights request has been skipped for this media. You're free to add some products and distribute this everywhere!",
    icon: ApprovedImage,
    source: RightsCreationSource.SkippedRightsRequest,
    status: RIGHTS_STATUS.APPROVED,
    title: 'Rights Skipped',
  },
];

export interface UIARequestStatusProps {
  cuid?: Cuid;
  expirationTime?: Date;
  isBVImport?: boolean;
  isItemSyndicated?: boolean;
  onCloseModal: () => void;
  onUpdateRights?: () => void;
  shouldEnableExpiryUpdate?: boolean;
  source?: RightsCreationSource;
  status: RIGHTS_STATUS;
}

export const UIARequestStatus = ({
  cuid,
  expirationTime,
  isBVImport,
  isItemSyndicated,
  onCloseModal,
  onUpdateRights,
  shouldEnableExpiryUpdate = false,
  source,
  status,
}: UIARequestStatusProps) => {
  const [shouldShowUpdateExpiryForm, setShouldShowUpdateExpiryForm] =
    useState(false);
  const config = rightsStatusConfigs.find((config) => {
    const conditions = [config.status === status, config.source === source];

    if (isBVImport) {
      conditions.push(config.isBVImport === isBVImport);
    }

    return conditions.every((condition) => condition);
  });

  if (isUndefined(config)) {
    return null;
  }

  if (!shouldShowUpdateExpiryForm) {
    return (
      <Wrapper>
        <Status>
          <Image alt={config.status} src={config.icon} />
          <TitleText>{config.title}</TitleText>
          <CenteredBodyText>{config.description}</CenteredBodyText>
          {shouldEnableExpiryUpdate && (
            <ExpiryInfo
              expirationTime={expirationTime}
              isDisabled={isItemSyndicated ?? true} // default set to true to make sure that we do not accidentally enable it on views where we do not pass isItemSyndicated. it should be enabled on screens only where the prop is explicitly passed as false
              onClick={() => setShouldShowUpdateExpiryForm(true)}
            />
          )}
        </Status>
        <Actions>
          <PrimaryButton
            onClick={onCloseModal}
            trackingId={UIA_RIGHTS_FLOW_TRACKING_IDS.RIGHTS_STATUS_DONE_BUTTON}
          >
            Done
          </PrimaryButton>
        </Actions>
      </Wrapper>
    );
  } else if (isDefined(cuid)) {
    return (
      <UIARightsSetExpiryScreen
        cuid={cuid}
        expiryTime={expirationTime}
        handleOnPreviousButtonClick={() => setShouldShowUpdateExpiryForm(false)}
        onUpdateRightsSuccess={() => {
          setShouldShowUpdateExpiryForm(false);
          onUpdateRights?.();
        }}
      />
    );
  } else {
    return null;
  }
};

const Wrapper = styled.div(
  () => `
    display: flex;
    flex-direction: column;
    align-items: center;
    height: 100%;
  `,
);

const Status = styled.div(
  ({ theme }) => `
    flex: 1;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    gap: ${theme.spacings[3]};
    padding: ${theme.spacings[6]};
  `,
);

const Image = styled.img(
  ({ theme }) => `
    height: auto;
    width: calc(3 * ${theme.spacings[6]});
  `,
);

const CenteredBodyText = styled(BodyText)`
  text-align: center;
`;

const Actions = styled.div(
  ({ theme }) => `
    display: flex;
    justify-content: flex-end;
    align-items: flex-end;
    padding: ${theme.spacings[3]};
    border-top: ${theme.borderWidths[1]} solid ${theme.colors.gray4};
    width: 100%;
  `,
);
